import { EVENTS_NAME } from '@src/consts/common';
import { useEventEmitter } from '@src/hooks/useEventEmitter';
import React, { useCallback, useEffect, useState } from 'react';
import { OnBoardingModal } from './_Modal/OnBoardingModal';
import { IOnBoardingModalSlideItem } from './_Slide/models';

function isUserAlreadySawOnboard(key: string) {
  const val = localStorage.getItem(key);
  return val && val === 'true';
}

interface IOnboardModalProps {
  /**
   * Ключ для локал сторадж, по которому будем понимаеть
   * проходили ли мы визард или надо его сейчас показать
   */
  id: string;

  /**
   * Сами слайды
   */
  slides: IOnBoardingModalSlideItem[];

  /**
   * Url перенаправления в случе успешного прохождения визарда
   * Если не передан, то модалка просто закрывается по окончании онбординга.
   */
  doneUrl?: string;

  /**
   * Некоторые случаи требуют, чтобы какие-то элементы были скрыти, чтобы не мешять отображенияю модального окна
   */
  hideCssSelectors?: string[];

  /**
   * Если не указано, то считается,
   * что открывать надо как только физически стартанут кампонент и мы еще не смотрели этот онбординг.
   *
   * Если указаны урлы, то будет показан только на этих урлах. Если указать пустой массив, модалка показываться не будет. Паттерны не поддеживаются - сравнение строгое.
   */
  urlsToAutoShow?: string[];

  /** Позволяет пропустить онбоардинг */
  canClose?: boolean;
}

/**
 * Отвечает за открытие и закрытие модального окна, сохранение общего состояния и скрытие
 * элементов по спец селекторам hideCssSelectors
 */
export function OnBoarding({
  id,
  slides,
  doneUrl,
  urlsToAutoShow,
  hideCssSelectors,
  canClose = false,
}: IOnboardModalProps) {
  const emitter = useEventEmitter();

  const isNeedOpenOnboardModal = (key: string): boolean => {
    if (isUserAlreadySawOnboard(key)) {
      return false;
    }
    if (!urlsToAutoShow) {
      return true;
    }

    const currentUrl = window.location.pathname + window.location.hash;
    for (const urlToAutoShow of urlsToAutoShow) {
      if (currentUrl === urlToAutoShow) {
        return true;
      }
    }
    return false;
  };

  const [isOpenModal, setIsOpenModal] = useState(false);

  useEffect(() => {
    setIsOpenModal(isNeedOpenOnboardModal(id));

    const eventHandler = (detail) => {
      const modalLocalStorageKey = detail.key;
      if (modalLocalStorageKey == id) {
        setIsOpenModal(true);
      }
    };

    emitter.on(EVENTS_NAME.OPEN_ON_BOARDING, eventHandler);
    return () => {
      emitter.off(EVENTS_NAME.OPEN_ON_BOARDING, eventHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitter, id]);

  useEffect(() => {
    if (hideCssSelectors) {
      for (const selector of hideCssSelectors) {
        const els = document.querySelectorAll(selector);
        for (let x = 0; x < els.length; x++) {
          if (isOpenModal) {
            // @ts-ignore
            els[x].style.display = 'none';
          } else {
            // @ts-ignore
            els[x].style.display = 'block';
          }
        }
      }
    }
  }, [isOpenModal, hideCssSelectors]);

  const handleOnClose = useCallback(
    (isDone) => {
      setIsOpenModal(false);
      if (isDone) {
        localStorage.setItem(id, JSON.stringify(true));
        if (doneUrl) {
          window.location.assign(doneUrl);
        }
      }
    },
    [doneUrl, id],
  );

  if (!isOpenModal) return null;

  return (
    <OnBoardingModal
      localStorageKey={id}
      open={isOpenModal}
      slides={slides}
      onClose={handleOnClose}
      canClose={canClose}
    />
  );
}
